import { groupBy, mapValues } from "lodash";
import {
  defineMessage,
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from "react-intl";
import { FolderCodeIcon } from "@/icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";
import ErrorPage from "@/pages/_error";
import { generatePath, Link } from "react-router";
import { Link as ButtonLink } from "@/kit/ui/link";
import { Environment, graphql } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { EntitySubmissionsPageQuery as EntitySubmissionsPageQueryType } from "./__generated__/EntitySubmissionsPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
export default function EntitySubmissionsPage() {
  const intl = useIntl();
  const {
    variables,
    query: { entityByUsername: user },
  } = useEntitySubmissionsPageQuery();
  if (!user) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  if (!user.submissions || user.submissions.nodes.length <= 0) {
    return <p className="text-gray-400">No submissions</p>;
  }
  const submissionCountByCompetition = mapValues(
    groupBy(user.submissions?.nodes, (node) =>
      node.stage.events.nodes.map((node) => node.competition.slug),
    ),
    (nodes) => nodes.length,
  );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <FormattedMessage defaultMessage="Challenge / Track" />
          </TableHead>
          <TableHead>
            <FormattedMessage defaultMessage="Submitted At" />
          </TableHead>
          <TableHead>
            <FormattedMessage defaultMessage="Score" />
          </TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {user.submissions?.nodes.map(
          ({ stage: { events, ...stage }, latest }) =>
            events.nodes.map(({ competition, event }) => {
              const submissionCountForCompetition =
                submissionCountByCompetition[competition.slug] ?? 1;
              return (
                <TableRow key={competition.slug}>
                  <TableCell className="whitespace-nowrap">
                    {submissionCountForCompetition > 1 ? (
                      <ButtonLink
                        to={generatePath("/challenges/:owner/tracks/:slug", {
                          owner: event.slug,
                          slug: competition.slug,
                        })}
                      >
                        <FormattedMessage
                          defaultMessage="{event} {competition} (stage {seq})"
                          values={{
                            event: event.slug,
                            competition: competition.title,
                            seq: stage.seq,
                          }}
                        />
                      </ButtonLink>
                    ) : (
                      <FormattedMessage
                        defaultMessage="<prev>{owner} / </prev> <next>{slug}</next>"
                        values={{
                          owner: event.slug,
                          slug: competition.title,
                          prev: (chunks) => (
                            <span className="text-grey-500 whitespace-nowrap">
                              <Link
                                to={generatePath("/challenges/:slug", {
                                  slug: event?.slug ?? "",
                                })}
                                className="hover:underline"
                              >
                                {chunks}
                              </Link>
                            </span>
                          ),
                          next: (chunks) => (
                            <span className="whitespace-nowrap">
                              <Link
                                to={generatePath(
                                  "/challenges/:owner/tracks/:slug",
                                  {
                                    owner: event.slug,
                                    slug: competition.slug,
                                  },
                                )}
                                className="hover:underline"
                              >
                                {chunks}
                              </Link>
                            </span>
                          ),
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {latest && (
                      <FormattedDate
                        value={latest.createdAt}
                        month="long"
                        day="numeric"
                        year="numeric"
                      />
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {competition.currentStage.showMetric &&
                      (typeof latest?.evaluation?.score === "number" ? (
                        <FormattedNumber value={latest.evaluation.score} />
                      ) : (
                        typeof latest?.evaluation?.error === "string" && (
                          <FormattedMessage defaultMessage="Error" />
                        )
                      ))}
                  </TableCell>
                  <TableCell className="text-end">
                    <ButtonLink
                      variant="secondary"
                      to={generatePath(
                        `/:username/submissions/:owner/:slug/latest/code/package?stage=${stage.seq}`,
                        {
                          username: variables.username,
                          owner: event.slug,
                          slug: competition.slug,
                        },
                      )}
                    >
                      <FolderCodeIcon />
                      <FormattedMessage defaultMessage="Browse" />
                    </ButtonLink>
                  </TableCell>
                </TableRow>
              );
            }),
        )}
      </TableBody>
    </Table>
  );
}
const EntitySubmissionsPageQuery = graphql`
  query EntitySubmissionsPageQuery($username: String!) {
    entityByUsername(username: $username) {
      submissions(allStages: true) {
        nodes {
          id
          stage {
            seq
            events {
              nodes {
                event {
                  slug
                }
                competition {
                  slug
                  title
                  banner
                  currentStage {
                    showMetric
                  }
                }
              }
            }
          }
          latest {
            files {
              downloadUrl @ifAllowed
            }
            createdAt
            evaluation {
              score
              error
            }
          }
        }
      }
    }
  }
`;
export function loader(environment: Environment) {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<EntitySubmissionsPageQueryType>(
      environment,
      EntitySubmissionsPageQuery,
      {
        username,
      },
    );
  };
}
const useEntitySubmissionsPageQuery = () =>
  usePreloaded<EntitySubmissionsPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Submissions" }),
};
